/* eslint-disable no-restricted-syntax */
const jsonParser = () => {
  // This is a function that can parse a JSON text, producing a JavaScript
  // data structure. It is a simple, recursive descent parser. It does not use
  // eval or regular expressions, so it can be used as a model for implementing
  // a JSON parser in other languages.

  // We are defining the function inside of another function to avoid creating
  // global variables.

  let at; // The index of the current character
  let ch; // The current character
  const escapee = {
    '"': '"',
    '\\': '\\',
    '/': '/',
    b: '\b',
    f: '\f',
    n: '\n',
    r: '\r',
    t: '\t',
  };
  let text;

  const error = (m) => {
    // Call error when something is wrong.

    throw new SyntaxError(m, 'file', at);
  };

  const next = (c) => {
    // If a c parameter is provided, verify that it matches the current character.

    if (c && c !== ch) {
      error(`Expected '${c}' instead of '${ch}'`);
    }

    // Get the next character. When there are no more characters,
    // return the empty string.

    ch = text.charAt(at);
    at += 1;
    return ch;
  };

  // eslint-disable-next-line consistent-return
  const parseNumber = () => {
    // Parse a number value.

    let string = '';

    if (ch === '-') {
      string = '-';
      next('-');
    }
    while (ch >= '0' && ch <= '9') {
      string += ch;
      next();
    }
    if (ch === '.') {
      string += '.';
      while (next() && ch >= '0' && ch <= '9') {
        string += ch;
      }
    }
    if (ch === 'e' || ch === 'E') {
      string += ch;
      next();
      if (ch === '-' || ch === '+') {
        string += ch;
        next();
      }
      while (ch >= '0' && ch <= '9') {
        string += ch;
        next();
      }
    }

    const number = +string;
    if (Number.isNaN(number)) {
      error('Bad number');
    } else {
      return number;
    }
  };

  // eslint-disable-next-line consistent-return
  const parseString = () => {
    // Parse a string value.

    let hex;
    let i;
    let string = '';
    let uffff;

    // When parsing for string values, we must look for " and \ characters.
    if (ch === '"') {
      while (next()) {
        if (ch === '"') {
          next();
          return string;
        }
        if (ch === '\\') {
          next();
          if (ch === 'u') {
            uffff = 0;
            for (i = 0; i < 4; i += 1) {
              hex = parseInt(next(), 16);
              if (!Number.isFinite(hex)) {
                break;
              }
              uffff = uffff * 16 + hex;
            }
            string += String.fromCharCode(uffff);
          } else if (typeof escapee[ch] === 'string') {
            string += escapee[ch];
          } else {
            break;
          }
        } else if (ch === '\n' || ch === '\r') {
          break;
        } else {
          string += ch;
        }
      }
    }
    error('Bad string');
  };

  const white = () => {
    // Skip whitespace.

    while (ch && ch <= ' ') {
      next();
    }
  };

  // eslint-disable-next-line consistent-return
  const word = () => {
    // true, false, or null.

    // eslint-disable-next-line default-case
    switch (ch) {
      case 't':
        next('t');
        next('r');
        next('u');
        next('e');
        return true;
      case 'f':
        next('f');
        next('a');
        next('l');
        next('s');
        next('e');
        return false;
      case 'n':
        next('n');
        next('u');
        next('l');
        next('l');
        return null;
    }
    error(`Unexpected '${ch}'`);
  };

  // eslint-disable-next-line consistent-return
  const parseArray = () => {
    // Parse an array value.

    const array = [];

    if (ch === '[') {
      next('[');
      white();
      if (ch === ']') {
        next(']');
        return array; // empty array
      }
      while (ch) {
        // eslint-disable-next-line no-use-before-define
        array.push(parseValue());
        white();
        if (ch === ']') {
          next(']');
          return array;
        }
        next(',');
        white();
      }
    }
    error('Bad array');
  };

  // eslint-disable-next-line consistent-return
  const parseObject = () => {
    // Parse an object value.

    let key;
    const object = {};

    if (ch === '{') {
      next('{');
      white();

      if (ch === '}') {
        next('}');
        return object; // empty object
      }

      while (ch) {
        key = parseString();
        white();
        next(':');
        if (Object.hasOwnProperty.call(object, key)) {
          error(`Duplicate key "${key}"`);
        }
        // eslint-disable-next-line no-use-before-define
        object[key] = parseValue();
        white();
        if (ch === '}') {
          next('}');
          return object;
        }
        next(',');
        white();
      }
    }
    error('Bad object');
  };

  const parseVariable = () => {
    next();
    while (ch !== '}') {
      next();
    }
    next();

    return '{}';
  };

  const parseValue = () => {
    // Parse a JSON value. It could be an object, an array, a string, a number,
    // or a word.
    white();
    const nextChar = text.charAt(at);
    if (nextChar.match(/\u00A0/)) {
      return parseVariable();
    }

    switch (ch) {
      case '{':
        return parseObject();
      case '[':
        return parseArray();
      case '"':
        return parseString();
      case '-':
        return parseNumber();
      default:
        return ch >= '0' && ch <= '9' ? parseNumber() : word();
    }
  };

  // Return the json_parse function. It will have access to all of the above
  // functions and variables.

  return (source, reviver) => {
    text = source;
    at = 0;
    ch = ' ';
    const result = parseValue();
    white();
    if (ch) {
      error('Syntax error');
    }

    // If there is a reviver function, we recursively walk the new structure,
    // passing each name/value pair to the reviver function for possible
    // transformation, starting with a temporary root object that holds the result
    // in an empty key. If there is not a reviver function, we simply return the
    // result.

    return typeof reviver === 'function' ? (function walk(holder, key) {
      let k; let v; const
        // eslint-disable-next-line no-shadow
        value = holder[key];
      if (value && typeof value === 'object') {
        for (k in value) {
          if (Object.hasOwnProperty.call(value, k)) {
            v = walk(value, k);
            if (v !== undefined) {
              value[k] = v;
            } else {
              delete value[k];
            }
          }
        }
      }
      return reviver.call(holder, key, value);
    }({ '': result }, '')) : result;
  };
};

export default jsonParser;
